import { BOOLEAN_FILTER_OPTIONS } from "@/components/ui/table/table-filter/filter-types/boolean-options";
import type { FilterTypeConfig } from "@/components/ui/table/table-filters";

import type {
  DefaultPolicy,
  FilterControl,
  FilterDefinition,
  FilterKey,
  FilterOperator,
  FilterOption,
  FilterQueryMetadata,
  LocationClearPolicy,
  QueryPolicy,
  UrlPolicy,
  ValueContract,
} from "./types";

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Seeds an empty location filter with the user's own scope, in the same order
 * the legacy `useDefaultLocationFilter` used: the location they're working at,
 * else the only location they have, else the set they're restricted to. A
 * full-access user with no current location gets no default and sees everything.
 */
export const LOCATION_SCOPE_DEFAULT: DefaultPolicy = [
  "currentLocation",
  "singleLocation",
  "scopedVisibleLocations",
];

export { BOOLEAN_FILTER_OPTIONS };

export function toSnakeCase(value: string): string {
  return value.replace(/[A-Z]/g, (character) => `_${character.toLowerCase()}`);
}

function resolveQueryMetadata<Request extends object>(
  requestKeys: readonly FilterKey<Request>[],
  defaultPolicy: QueryPolicy,
  query: FilterQueryMetadata<Request> | false | undefined,
  queryKeys?: readonly string[],
  queryPolicy?: QueryPolicy,
): FilterQueryMetadata<Request> | false {
  if (query === false) return false;
  return {
    ...query,
    keys:
      queryKeys ??
      query?.keys ??
      requestKeys.map((requestKey) => toSnakeCase(requestKey)),
    policy: queryPolicy ?? query?.policy ?? defaultPolicy,
  };
}

function appendQueryValue(
  params: URLSearchParams,
  key: string,
  value: unknown,
  policy: QueryPolicy,
): void {
  if (
    policy === "none" ||
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return;
  }
  if (policy === "repeated" && Array.isArray(value)) {
    value.forEach((item) => appendQueryValue(params, key, item, "scalar"));
    return;
  }
  if (
    policy === "json" ||
    (typeof value === "object" && !Array.isArray(value))
  ) {
    params.append(key, JSON.stringify(value));
    return;
  }
  params.append(key, String(value));
}

function writeDefinitionQuery<Request extends object>(
  definition: FilterDefinition<Request>,
  params: URLSearchParams,
  request: Partial<Request>,
): void {
  if (definition.query === false) return;
  if (definition.query?.serialize) {
    definition.query.serialize({ params, request, definition });
    return;
  }
  const keys =
    definition.query?.keys ??
    definition.requestKeys.map((requestKey) => toSnakeCase(requestKey));
  const policy = definition.query?.policy ?? "scalar";
  definition.requestKeys.forEach((requestKey, index) => {
    appendQueryValue(
      params,
      keys[index] ?? toSnakeCase(requestKey),
      (request as MutableRequest<Request>)[requestKey],
      policy,
    );
  });
}

type MutableRequest<_Request extends object> = Record<string, any>;

function asMutable<Request extends object>(
  request: Partial<Request>,
): MutableRequest<Request> {
  return { ...request } as MutableRequest<Request>;
}

function normalizeString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized || undefined;
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function normalizeDate(value: unknown): string | undefined {
  const normalized = normalizeString(value);
  if (!normalized || !DATE_PATTERN.test(normalized)) return undefined;
  const [year, month, day] = normalized.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? normalized
    : undefined;
}

export function normalizeSet<Value extends string>(
  value: unknown,
  allowedValues?: ReadonlySet<string>,
): Value[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const normalized = value.map(normalizeString);
  if (
    normalized.some(
      (item) =>
        item === undefined || (allowedValues && !allowedValues.has(item)),
    )
  ) {
    return undefined;
  }
  const unique = Array.from(new Set(normalized as string[])).sort();
  return unique.length ? (unique as Value[]) : undefined;
}

function repeatedValues(params: URLSearchParams, key: string): string[] {
  return params.getAll(key);
}

function replaceRepeated(
  params: URLSearchParams,
  key: string,
  values: readonly string[] | undefined,
): void {
  params.delete(key);
  values?.forEach((value) => params.append(key, value));
}

function scalarUrlValue(
  params: URLSearchParams,
  key: string,
): string | undefined {
  const values = params.getAll(key);
  return values.length ? values[values.length - 1] : undefined;
}

interface ScalarFilterArgs<Request extends object> {
  key: FilterKey<Request>;
  label: string;
  operator?: FilterOperator;
  control?: FilterControl;
  urlPolicy?: UrlPolicy;
  urlKey?: string;
  options?: readonly FilterOption[];
  presented?: boolean;
  normalize?: (value: unknown) => unknown;
  valueKind?: "string" | "number" | "boolean" | "enum";
  queryKey?: string;
  queryPolicy?: QueryPolicy;
  query?: FilterQueryMetadata<Request> | false;
}

function scalarFilter<Request extends object>({
  key,
  label,
  operator = "eq",
  control = "text",
  urlPolicy = "scalar",
  urlKey = key,
  options,
  presented = true,
  normalize = normalizeString,
  valueKind = "string",
  queryKey,
  queryPolicy,
  query,
}: ScalarFilterArgs<Request>): FilterDefinition<Request> {
  const contract: ValueContract<unknown> = { normalize };
  const definition: FilterDefinition<Request> = {
    key,
    requestKeys: [key],
    urlKeys: [urlKey],
    label,
    value: valueKind,
    operator,
    control,
    contract,
    urlPolicy,
    options,
    presented,
    query: resolveQueryMetadata(
      [key],
      "scalar",
      query,
      queryKey ? [queryKey] : undefined,
      queryPolicy,
    ),
    normalizeRequest(request) {
      const next = asMutable(request);
      const value = normalize(next[key]);
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next as Partial<Request>;
    },
    writeQuery(params, request) {
      writeDefinitionQuery(definition, params, request);
    },
    parseUrl(params) {
      if (urlPolicy === "none") return {};
      const raw = scalarUrlValue(params, urlKey);
      if (raw === undefined) return {};
      const value =
        valueKind === "boolean"
          ? raw === "true"
            ? true
            : raw === "false"
              ? false
              : undefined
          : normalize(raw);
      return value === undefined ? {} : ({ [key]: value } as Partial<Request>);
    },
    writeUrl(params, request) {
      if (urlPolicy === "none") return;
      params.delete(urlKey);
      const value = normalize((request as MutableRequest<Request>)[key]);
      if (value !== undefined) params.append(urlKey, String(value));
    },
    readControl(request) {
      return (request as MutableRequest<Request>)[key];
    },
    writeControl(request, value) {
      return { ...request, [key]: value } as Partial<Request>;
    },
  };
  return definition;
}

export function textFilter<Request extends object>(
  args: Omit<ScalarFilterArgs<Request>, "valueKind" | "normalize">,
): FilterDefinition<Request> {
  return scalarFilter({
    ...args,
    valueKind: "string",
    normalize: normalizeString,
  });
}

export function enumFilter<Request extends object>({
  extraValues,
  ...args
}: Omit<ScalarFilterArgs<Request>, "valueKind" | "normalize"> & {
  options: readonly FilterOption[];
  /** Also accepted, asked each time a value is checked, for values that depend on where the filter is shown. */
  extraValues?: () => readonly string[];
}): FilterDefinition<Request> {
  const allowed = new Set(args.options.map((option) => option.value));
  return scalarFilter({
    ...args,
    valueKind: "enum",
    normalize: (value) => {
      const normalized = normalizeString(value);
      if (!normalized) return undefined;
      return allowed.has(normalized) || extraValues?.().includes(normalized)
        ? normalized
        : undefined;
    },
  });
}

/**
 * A true/false request value. With `control: "boolean"` it shows as an
 * Any / Yes / No control, which speaks the strings "true"/"false"; the
 * request keeps real booleans. Pass `options` to relabel (e.g. Active/Paused).
 */
export function booleanFilter<Request extends object>(
  args: Omit<
    ScalarFilterArgs<Request>,
    "valueKind" | "normalize" | "options"
  > & {
    // The control writes back the chosen option's value, and only these two
    // strings map to the request's true/false; relabel, don't revalue.
    options?: readonly FilterOption<"true" | "false">[];
  },
): FilterDefinition<Request> {
  const definition = scalarFilter({
    ...args,
    valueKind: "boolean",
    normalize: normalizeBoolean,
    control: args.control ?? "none",
    // An empty list falls back too: the control shows Yes/No for it.
    options: args.options?.length
      ? args.options
      : args.control === "boolean"
        ? BOOLEAN_FILTER_OPTIONS
        : args.options,
  });
  if (definition.control !== "boolean") return definition;
  return {
    ...definition,
    readControl(request) {
      const value = (request as MutableRequest<Request>)[args.key];
      return typeof value === "boolean" ? String(value) : undefined;
    },
    writeControl(request, value) {
      const next =
        value === true || value === "true"
          ? true
          : value === false || value === "false"
            ? false
            : undefined;
      return { ...request, [args.key]: next } as Partial<Request>;
    },
  };
}

export function numberFilter<Request extends object>(
  args: Omit<ScalarFilterArgs<Request>, "valueKind" | "normalize">,
): FilterDefinition<Request> {
  return scalarFilter({
    ...args,
    valueKind: "number",
    normalize: normalizeNumber,
    control: args.control ?? "none",
  });
}

interface SetFilterArgs<Request extends object> {
  key: FilterKey<Request>;
  label: string;
  operator?: "anyOf" | "allOf";
  control?: "multiSelect" | "location";
  urlPolicy?: UrlPolicy;
  urlKey?: string;
  options?: readonly FilterOption[];
  presented?: boolean;
  queryKey?: string;
  queryPolicy?: QueryPolicy;
  query?: FilterQueryMetadata<Request> | false;
  /** Location sets only: how an empty value is seeded and cleared. */
  defaultPolicy?: DefaultPolicy;
  clearPolicy?: LocationClearPolicy;
}

export function setFilter<Request extends object>({
  key,
  label,
  operator = "anyOf",
  control = "multiSelect",
  urlPolicy = "boundedSet",
  urlKey = key,
  options,
  presented = true,
  queryKey,
  queryPolicy,
  query,
  defaultPolicy,
  clearPolicy,
}: SetFilterArgs<Request>): FilterDefinition<Request> {
  const contract: ValueContract<string[]> = { normalize: normalizeSet };
  const definition: FilterDefinition<Request> = {
    key,
    requestKeys: [key],
    urlKeys: [urlKey],
    label,
    value: "set",
    operator,
    control,
    contract,
    urlPolicy,
    options,
    presented,
    defaultPolicy,
    clearPolicy,
    query: resolveQueryMetadata(
      [key],
      "repeated",
      query,
      queryKey ? [queryKey] : undefined,
      queryPolicy,
    ),
    normalizeRequest(request) {
      const next = asMutable(request);
      const value = normalizeSet(next[key]);
      if (value) next[key] = value;
      else delete next[key];
      return next as Partial<Request>;
    },
    writeQuery(params, request) {
      writeDefinitionQuery(definition, params, request);
    },
    parseUrl(params) {
      if (urlPolicy === "none") return {};
      const value = normalizeSet(repeatedValues(params, urlKey));
      return value ? ({ [key]: value } as Partial<Request>) : {};
    },
    writeUrl(params, request) {
      if (urlPolicy === "none") return;
      replaceRepeated(
        params,
        urlKey,
        normalizeSet((request as MutableRequest<Request>)[key]),
      );
    },
    readControl(request) {
      return (request as MutableRequest<Request>)[key];
    },
    writeControl(request, value) {
      return { ...request, [key]: value } as Partial<Request>;
    },
  };
  return definition;
}

interface TupleRangeFilterArgs<Request extends object> {
  key: FilterKey<Request>;
  label: string;
  urlKey?: string;
  queryKey?: string;
  urlPolicy?: UrlPolicy;
  presented?: boolean;
  query?: FilterQueryMetadata<Request> | false;
}

function tupleRangeFilter<
  Request extends object,
  Bound extends string | number,
>(
  {
    key,
    label,
    urlKey = key,
    queryKey = toSnakeCase(key),
    urlPolicy = "boundedSet",
    presented = true,
    query,
  }: TupleRangeFilterArgs<Request>,
  value: "dateRange" | "range",
  control: "dateRange" | "numberRange",
  normalizeBound: (bound: unknown) => Bound | undefined,
  { ordered = true }: { ordered?: boolean } = {},
): FilterDefinition<Request> {
  const normalize = (raw: unknown): (Bound | null)[] | undefined => {
    if (!Array.isArray(raw) || raw.length !== 2) return undefined;
    const tuple = raw.map((bound) => {
      if (bound === null || bound === undefined || bound === "") return null;
      return normalizeBound(bound);
    });
    if (tuple.some((bound) => bound === undefined)) return undefined;
    if (tuple.every((bound) => bound === null)) return undefined;
    const normalizedTuple = tuple as (Bound | null)[];
    const [lower, upper] = normalizedTuple;
    if (
      ordered &&
      lower !== null &&
      upper !== null &&
      typeof lower === typeof upper &&
      lower > upper
    ) {
      return undefined;
    }
    return normalizedTuple;
  };
  const definition: FilterDefinition<Request> = {
    key,
    requestKeys: [key],
    urlKeys: [urlKey],
    label,
    value,
    operator: "between",
    control,
    contract: { normalize },
    urlPolicy,
    presented,
    query:
      query === false
        ? false
        : { keys: [queryKey], policy: "repeated", ...query },
    normalizeRequest(request) {
      const next = asMutable(request);
      const tuple = normalize(next[key]);
      if (tuple) next[key] = tuple;
      else delete next[key];
      return next as Partial<Request>;
    },
    writeQuery(params, request) {
      if (definition.query === false) return;
      if (definition.query?.serialize) {
        definition.query.serialize({ params, request, definition });
        return;
      }
      // Both bounds are always emitted so an open bound stays positional.
      const targetKey = definition.query?.keys?.[0] ?? queryKey;
      const tuple = normalize((request as MutableRequest<Request>)[key]);
      tuple?.forEach((bound) =>
        params.append(targetKey, bound === null ? "" : String(bound)),
      );
    },
    parseUrl(params) {
      if (urlPolicy === "none") return {};
      const tuple = normalize(repeatedValues(params, urlKey));
      return tuple ? ({ [key]: tuple } as Partial<Request>) : {};
    },
    writeUrl(params, request) {
      if (urlPolicy === "none") return;
      params.delete(urlKey);
      const tuple = normalize((request as MutableRequest<Request>)[key]);
      tuple?.forEach((bound) =>
        params.append(urlKey, bound === null ? "" : String(bound)),
      );
    },
    readControl(request) {
      return normalize((request as MutableRequest<Request>)[key]);
    },
    writeControl(request, controlValue) {
      return { ...request, [key]: controlValue } as Partial<Request>;
    },
  };
  return definition;
}

export function tupleDateRangeFilter<Request extends object>(
  args: TupleRangeFilterArgs<Request>,
): FilterDefinition<Request> {
  return tupleRangeFilter(args, "dateRange", "dateRange", normalizeDate);
}

export function tupleNumberRangeFilter<Request extends object>(
  args: TupleRangeFilterArgs<Request>,
): FilterDefinition<Request> {
  return tupleRangeFilter(args, "range", "numberRange", normalizeNumber);
}

const MONTH_DAY_PATTERN = /^--(\d{2})-(\d{2})$/;

function normalizeMonthDay(value: unknown): string | undefined {
  const normalized = normalizeString(value);
  const match = normalized ? MONTH_DAY_PATTERN.exec(normalized) : null;
  if (!match) return undefined;
  const month = Number(match[1]);
  const day = Number(match[2]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  return normalized;
}

/**
 * A yearless `--MM-DD` range, for recurring dates like birthdays.
 *
 * Unordered on purpose: a birthday window legitimately wraps the year end
 * (`--12-25` to `--01-05`), so the usual lower-must-precede-upper check would
 * reject valid ranges.
 */
export function tupleMonthDayRangeFilter<Request extends object>(
  args: TupleRangeFilterArgs<Request>,
): FilterDefinition<Request> {
  return tupleRangeFilter(args, "dateRange", "dateRange", normalizeMonthDay, {
    ordered: false,
  });
}

export function enumSetFilter<Request extends object>(
  args: SetFilterArgs<Request> & { options: readonly FilterOption[] },
): FilterDefinition<Request> {
  const allowed = new Set(args.options.map((option) => option.value));
  const definition = setFilter(args);
  const normalize = (value: unknown) => normalizeSet(value, allowed);
  return {
    ...definition,
    contract: { normalize },
    normalizeRequest(request) {
      const next = asMutable(request);
      const value = normalize(next[args.key]);
      if (value) next[args.key] = value;
      else delete next[args.key];
      return next as Partial<Request>;
    },
    parseUrl(params) {
      if ((args.urlPolicy ?? "boundedSet") === "none") return {};
      const value = normalize(repeatedValues(params, args.urlKey ?? args.key));
      return value ? ({ [args.key]: value } as Partial<Request>) : {};
    },
  };
}

/**
 * A yes/no filter whose request value stays a one-item list (`["true"]`),
 * for endpoints that read the key as a repeated param and may also accept a
 * third choice such as "not_set". Shown as an Any / Yes / No control, so it
 * holds at most one choice: a stored or bookmarked list of several reads as
 * Any.
 */
export function booleanSetFilter<Request extends object>(
  args: Omit<SetFilterArgs<Request>, "control" | "operator" | "options"> & {
    options?: readonly FilterOption[];
  },
): FilterDefinition<Request> {
  // An empty list falls back too: the control shows Yes/No for it.
  const options = args.options?.length ? args.options : BOOLEAN_FILTER_OPTIONS;
  const definition = enumSetFilter({ ...args, options });
  const single = (value: unknown) => {
    const set = definition.contract.normalize(value) as string[] | undefined;
    return set?.length === 1 ? set : undefined;
  };
  // Only this helper renders a list-shaped filter as the boolean control,
  // because its read and write below turn the list into one choice.
  return {
    ...definition,
    control: "boolean",
    contract: { normalize: single },
    normalizeRequest(request) {
      const next = asMutable(request);
      const value = single(next[args.key]);
      if (value) next[args.key] = value;
      else delete next[args.key];
      return next as Partial<Request>;
    },
    parseUrl(params) {
      if ((args.urlPolicy ?? "boundedSet") === "none") return {};
      const value = single(repeatedValues(params, args.urlKey ?? args.key));
      return value ? ({ [args.key]: value } as Partial<Request>) : {};
    },
    readControl(request) {
      return single((request as MutableRequest<Request>)[args.key])?.[0];
    },
    writeControl(request, value) {
      const next =
        value === undefined || value === null || value === ""
          ? undefined
          : single([String(value)]);
      return { ...request, [args.key]: next } as Partial<Request>;
    },
  };
}

interface LocationSetFilterArgs<Request extends object> extends Omit<
  SetFilterArgs<Request>,
  "control"
> {
  includeUnassignedKey: FilterKey<Request>;
  includeUnassignedUrlKey?: string;
  includeUnassignedQueryKey?: string;
  includeUnassignedWhenActive?: boolean;
}

export function locationSetFilter<Request extends object>({
  includeUnassignedKey,
  includeUnassignedUrlKey = includeUnassignedKey,
  includeUnassignedQueryKey = toSnakeCase(includeUnassignedKey),
  defaultPolicy = "none",
  clearPolicy = "empty",
  includeUnassignedWhenActive = false,
  ...args
}: LocationSetFilterArgs<Request>): FilterDefinition<Request> {
  const definition = setFilter({
    ...args,
    control: "location",
    defaultPolicy,
    clearPolicy,
  });
  const locationDefinition: FilterDefinition<Request> = {
    ...definition,
    requestKeys: [args.key, includeUnassignedKey],
    urlKeys: [...definition.urlKeys, includeUnassignedUrlKey],
    query: resolveQueryMetadata(
      [args.key, includeUnassignedKey],
      "repeated",
      args.query,
      [args.queryKey ?? toSnakeCase(args.key), includeUnassignedQueryKey],
      args.queryPolicy,
    ),
    normalizeRequest(request) {
      const next = asMutable(definition.normalizeRequest(request));
      const includeUnassigned = normalizeBoolean(next[includeUnassignedKey]);
      if (includeUnassigned === undefined) delete next[includeUnassignedKey];
      else next[includeUnassignedKey] = includeUnassigned;
      return next as Partial<Request>;
    },
    writeQuery(params, request) {
      writeDefinitionQuery(locationDefinition, params, request);
    },
    parseUrl(params) {
      if ((args.urlPolicy ?? "boundedSet") === "none") return {};
      const parsed = definition.parseUrl(params);
      const raw = scalarUrlValue(params, includeUnassignedUrlKey);
      if (raw === "true" || raw === "false") {
        return {
          ...parsed,
          [includeUnassignedKey]: raw === "true",
        } as Partial<Request>;
      }
      return parsed;
    },
    writeUrl(params, request) {
      if ((args.urlPolicy ?? "boundedSet") === "none") return;
      definition.writeUrl(params, request);
      params.delete(includeUnassignedUrlKey);
      const value = normalizeBoolean(
        (request as MutableRequest<Request>)[includeUnassignedKey],
      );
      const ids = normalizeSet((request as MutableRequest<Request>)[args.key]);
      if (value === true || ids) {
        params.append(includeUnassignedUrlKey, String(value ?? false));
      }
    },
    writeControl(request, value) {
      const ids = normalizeSet(value);
      return {
        ...request,
        [args.key]: ids,
        [includeUnassignedKey]: ids ? includeUnassignedWhenActive : false,
      } as Partial<Request>;
    },
  };
  return locationDefinition;
}

interface DateRangeFilterArgs<Request extends object> {
  startKey: FilterKey<Request>;
  endKey: FilterKey<Request>;
  label: string;
  startUrlKey?: string;
  endUrlKey?: string;
  startQueryKey?: string;
  endQueryKey?: string;
  queryPolicy?: QueryPolicy;
  query?: FilterQueryMetadata<Request> | false;
  urlPolicy?: UrlPolicy;
  presented?: boolean;
}

export function dateRangeFilter<Request extends object>({
  startKey,
  endKey,
  label,
  startUrlKey = startKey,
  endUrlKey = endKey,
  startQueryKey = toSnakeCase(startKey),
  endQueryKey = toSnakeCase(endKey),
  queryPolicy,
  query,
  urlPolicy = "scalar",
  presented = true,
}: DateRangeFilterArgs<Request>): FilterDefinition<Request> {
  const normalize = (value: unknown) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return undefined;
    }
    const range = value as { start?: unknown; end?: unknown };
    const start = normalizeDate(range.start);
    const end = normalizeDate(range.end);
    if (!start && !end) return undefined;
    if (start && end && start > end) return undefined;
    return { start, end };
  };
  const definition: FilterDefinition<Request> = {
    key: startKey,
    requestKeys: [startKey, endKey],
    urlKeys: [startUrlKey, endUrlKey],
    label,
    value: "dateRange",
    operator: "between",
    control: "dateRange",
    contract: { normalize },
    urlPolicy,
    presented,
    query: resolveQueryMetadata(
      [startKey, endKey],
      "scalar",
      query,
      [startQueryKey, endQueryKey],
      queryPolicy,
    ),
    normalizeRequest(request) {
      const next = asMutable(request);
      const range = normalize({ start: next[startKey], end: next[endKey] });
      if (!range) {
        delete next[startKey];
        delete next[endKey];
      } else {
        if (range.start) next[startKey] = range.start;
        else delete next[startKey];
        if (range.end) next[endKey] = range.end;
        else delete next[endKey];
      }
      return next as Partial<Request>;
    },
    writeQuery(params, request) {
      writeDefinitionQuery(definition, params, request);
    },
    parseUrl(params) {
      if (urlPolicy === "none") return {};
      const range = normalize({
        start: scalarUrlValue(params, startUrlKey),
        end: scalarUrlValue(params, endUrlKey),
      });
      if (!range) return {};
      return {
        ...(range.start ? { [startKey]: range.start } : {}),
        ...(range.end ? { [endKey]: range.end } : {}),
      } as Partial<Request>;
    },
    writeUrl(params, request) {
      if (urlPolicy === "none") return;
      params.delete(startUrlKey);
      params.delete(endUrlKey);
      const range = normalize({
        start: (request as MutableRequest<Request>)[startKey],
        end: (request as MutableRequest<Request>)[endKey],
      });
      if (range?.start) params.append(startUrlKey, range.start);
      if (range?.end) params.append(endUrlKey, range.end);
    },
    readControl(request) {
      const start = normalizeDate(
        (request as MutableRequest<Request>)[startKey],
      );
      const end = normalizeDate((request as MutableRequest<Request>)[endKey]);
      return start || end ? [start, end] : undefined;
    },
    writeControl(request, value) {
      const values = Array.isArray(value) ? value : [];
      return {
        ...request,
        [startKey]: values[0],
        [endKey]: values[1],
      } as Partial<Request>;
    },
  };
  return definition;
}

interface NumberRangeFilterArgs<Request extends object> {
  minKey: FilterKey<Request>;
  maxKey: FilterKey<Request>;
  label: string;
  minUrlKey?: string;
  maxUrlKey?: string;
  minQueryKey?: string;
  maxQueryKey?: string;
  queryPolicy?: QueryPolicy;
  query?: FilterQueryMetadata<Request> | false;
  urlPolicy?: UrlPolicy;
  presented?: boolean;
}

function normalizeNumber(value: unknown): number | undefined {
  if (value === "" || value === null || value === undefined) return undefined;
  const normalized = typeof value === "number" ? value : Number(value);
  return Number.isFinite(normalized) ? normalized : undefined;
}

export function numberRangeFilter<Request extends object>({
  minKey,
  maxKey,
  label,
  minUrlKey = minKey,
  maxUrlKey = maxKey,
  minQueryKey = toSnakeCase(minKey),
  maxQueryKey = toSnakeCase(maxKey),
  queryPolicy,
  query,
  urlPolicy = "scalar",
  presented = true,
}: NumberRangeFilterArgs<Request>): FilterDefinition<Request> {
  const normalize = (value: unknown) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return undefined;
    }
    const range = value as { min?: unknown; max?: unknown };
    const min = normalizeNumber(range.min);
    const max = normalizeNumber(range.max);
    if (min === undefined && max === undefined) return undefined;
    if (min !== undefined && max !== undefined && min > max) return undefined;
    return { min, max };
  };
  const definition: FilterDefinition<Request> = {
    key: minKey,
    requestKeys: [minKey, maxKey],
    urlKeys: [minUrlKey, maxUrlKey],
    label,
    value: "range",
    operator: "between",
    control: "numberRange",
    contract: { normalize },
    urlPolicy,
    presented,
    query: resolveQueryMetadata(
      [minKey, maxKey],
      "scalar",
      query,
      [minQueryKey, maxQueryKey],
      queryPolicy,
    ),
    normalizeRequest(request) {
      const next = asMutable(request);
      const range = normalize({ min: next[minKey], max: next[maxKey] });
      if (!range) {
        delete next[minKey];
        delete next[maxKey];
      } else {
        if (range.min !== undefined) next[minKey] = range.min;
        else delete next[minKey];
        if (range.max !== undefined) next[maxKey] = range.max;
        else delete next[maxKey];
      }
      return next as Partial<Request>;
    },
    writeQuery(params, request) {
      writeDefinitionQuery(definition, params, request);
    },
    parseUrl(params) {
      if (urlPolicy === "none") return {};
      const range = normalize({
        min: scalarUrlValue(params, minUrlKey),
        max: scalarUrlValue(params, maxUrlKey),
      });
      if (!range) return {};
      return {
        ...(range.min !== undefined ? { [minKey]: range.min } : {}),
        ...(range.max !== undefined ? { [maxKey]: range.max } : {}),
      } as Partial<Request>;
    },
    writeUrl(params, request) {
      if (urlPolicy === "none") return;
      params.delete(minUrlKey);
      params.delete(maxUrlKey);
      const range = normalize({
        min: (request as MutableRequest<Request>)[minKey],
        max: (request as MutableRequest<Request>)[maxKey],
      });
      if (range?.min !== undefined) params.append(minUrlKey, String(range.min));
      if (range?.max !== undefined) params.append(maxUrlKey, String(range.max));
    },
    readControl(request) {
      const min = normalizeNumber((request as MutableRequest<Request>)[minKey]);
      const max = normalizeNumber((request as MutableRequest<Request>)[maxKey]);
      return min !== undefined || max !== undefined ? [min, max] : undefined;
    },
    writeControl(request, value) {
      const values = Array.isArray(value) ? value : [];
      return {
        ...request,
        [minKey]: values[0],
        [maxKey]: values[1],
      } as Partial<Request>;
    },
  };
  return definition;
}

/** Async-safe entity IDs: options are presentation data, not validation. */
export function entitySetFilter<Request extends object>(
  args: SetFilterArgs<Request>,
): FilterDefinition<Request> {
  return setFilter(args);
}

/**
 * One id or several (`?id=a&id=b`, as a link to a combined notification's rows
 * names them). A single id, typed or from an old link, is a list of one. Names
 * exact rows, so it skips the location default.
 */
export function idSetFilter<Request extends object>(
  args: Pick<SetFilterArgs<Request>, "key" | "label">,
): FilterDefinition<Request> {
  const { key } = args;
  const toSet = (value: unknown) =>
    normalizeSet(typeof value === "string" ? [value] : value);
  const base = setFilter<Request>(args);
  const definition: FilterDefinition<Request> = {
    ...base,
    contract: { normalize: toSet },
    bypassesLocationDefaults: true,
    normalizeRequest(request) {
      const next = asMutable(request);
      const value = toSet(next[key]);
      if (value) next[key] = value;
      else delete next[key];
      return next as Partial<Request>;
    },
    writeUrl(params, request) {
      replaceRepeated(
        params,
        key,
        toSet((request as MutableRequest<Request>)[key]),
      );
    },
    writeControl(request, value) {
      return { ...request, [key]: toSet(value) } as Partial<Request>;
    },
  };
  return definition;
}

export function defineFilters<Request extends object>() {
  return <Definitions extends readonly FilterDefinition<Request>[]>(
    definitions: Definitions,
  ): Definitions => definitions;
}

const CONTROL_TYPE: Record<
  Exclude<FilterControl, "none">,
  FilterTypeConfig["type"]
> = {
  text: "text",
  select: "select",
  multiSelect: "multiselect",
  boolean: "boolean",
  location: "location",
  provider: "provider",
  dateRange: "date",
  numberRange: "number-range",
};

export function toTableFilterTypes<Request extends object>(
  definitions: readonly FilterDefinition<Request>[],
): FilterTypeConfig[] {
  return definitions.flatMap((definition) => {
    if (!definition.presented || definition.control === "none") return [];
    return [
      {
        filterKey: definition.key,
        label: definition.label,
        type: CONTROL_TYPE[definition.control],
        options: definition.options?.map((option) => ({ ...option })),
      },
    ];
  });
}
